import type {
  AgendaState,
  Booking,
} from "../types";


type AgendaGridProps = {
  agenda: AgendaState;

  visibleHours: number[];

  bookingsToday: Booking[];

  canCreate: boolean;

  formatHour: (
    hour: number,
  ) => string;

  onFreeSlotClick: (
    court: string,
    hour: number,
  ) => void;

  onBookingClick: (
    booking: Booking,
  ) => void;
};


function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(value);
}


function formatDuration(
  duration: number,
) {
  if (duration === 0.5) {
    return "30min";
  }

  if (duration === 1) {
    return "1h";
  }

  if (duration === 1.5) {
    return "1h30";
  }

  if (
    Number.isInteger(
      duration,
    )
  ) {
    return `${duration}h`;
  }

  const hours =
    Math.floor(
      duration,
    );

  return `${hours}h30`;
}


export default function AgendaGrid({
  agenda,
  visibleHours,
  bookingsToday,
  canCreate,
  formatHour,
  onFreeSlotClick,
  onBookingClick,
}: AgendaGridProps) {
  function bookingAt(
    court: string,
    hour: number,
  ) {
    return bookingsToday.find(
      (booking) =>
        booking.court ===
          court &&
        hour >=
          booking.hour &&
        hour <
          booking.hour +
            booking.duration,
    );
  }


  function isBookingStart(
    booking: Booking,
    hour: number,
  ) {
    return (
      booking.hour ===
      hour
    );
  }


  return (
    <div
      className="agenda-grid"
      style={{
        gridTemplateColumns:
          `80px repeat(${agenda.courts.length}, minmax(190px, 1fr))`,
      }}
    >

      <div className="agenda-corner" />
      
      {agenda.courts.map(
        (court) => (
          <div
            key={court}
            className="court-header"
          >
            {court}
          </div>
        ),
      )}


      {visibleHours.map(
        (hour) => (
          <div
            key={hour}
            style={{
              display:
                "contents",
            }}
          >
            <div className="hour-label">
              {formatHour(
                hour,
              )}
            </div>


            {agenda.courts.map(
              (court) => {
                const booking =
                  bookingAt(
                    court,
                    hour,
                  );


                if (
                  booking &&
                  !isBookingStart(
                    booking,
                    hour,
                  )
                ) {
                  return (
                    <div
                      key={`${court}-${hour}`}
                      className={
                        booking.kind ===
                        "aula"
                          ? "slot occupied-continuation lesson-continuation"
                          : "slot occupied-continuation"
                      }
                    />
                  );
                }


                return (
                  <div
                    key={`${court}-${hour}`}
                    className={
                      booking
                        ? booking.kind ===
                          "aula"
                          ? "slot occupied lesson-slot"
                          : "slot occupied"
                        : "slot free"
                    }
                  >
                    {booking ? (
                      booking.kind ===
                      "aula" ? (
                        /*
                         * ==========================================
                         * AULA RECORRENTE
                         * ==========================================
                         *
                         * Não abre BookingModal.
                         * Não possui cobrança de quadra.
                         * Apenas ocupa o horário.
                         */
                        <div className="lesson-card">
                          <div className="lesson-card-label">
                            AULA
                          </div>

                          <strong>
                            {booking.turma_nome ||
                              booking.cliente}
                          </strong>

                          {booking.professor && (
                            <span>
                              Prof.{" "}
                              {
                                booking.professor
                              }
                            </span>
                          )}

                          <span>
                            {formatHour(
                              booking.hour,
                            )}{" "}
                            ·{" "}
                            {formatDuration(
                              booking.duration,
                            )}
                          </span>

                          <small>
                            {booking.tipo_turma ===
                            "externa"
                              ? "Professor externo"
                              : "Turma da Arena"}
                          </small>
                        </div>
                      ) : (
                        /*
                         * ==========================================
                         * RESERVA NORMAL
                         * ==========================================
                         */
                        <button
                          type="button"
                          className={`booking-card booking-card-button ${
                            booking.pago
                              ? "booking-card-paid"
                              : "booking-card-pending"
                          }`}
                          onClick={() =>
                            onBookingClick(
                              booking,
                            )
                          }
                        >
                          <strong>
                            {
                              booking.cliente
                            }
                          </strong>

                          <span>
                            {formatHour(
                              booking.hour,
                            )}{" "}
                            ·{" "}
                            {formatDuration(
                              booking.duration,
                            )}
                          </span>

                          <span className="booking-card-price">
                            {formatBRL(
                              Number(
                                booking.price ??
                                  0,
                              ),
                            )}
                          </span>

                          <small>
                            {booking.pago
                              ? "Pago"
                              : "Pendente"}
                          </small>
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        className="free-slot"
                        disabled={
                          !canCreate
                        }
                        onClick={() =>
                          onFreeSlotClick(
                            court,
                            hour,
                          )
                        }
                      >
                        + Livre
                      </button>
                    )}
                  </div>
                );
              },
            )}
          </div>
        ),
      )}
    </div>
  );
}